import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authApi } from '../services/api';
import SafeIcon from '../common/SafeIcon';
import { 
  FiLock, FiMail, FiEye, FiEyeOff, FiCheck, 
  FiHelpCircle, FiLoader, FiAlertCircle, FiArrowRight, FiShield 
} from 'react-icons/fi';

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [passcode, setPasscode] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPasscode, setShowPasscode] = useState(false);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [stage, setStage] = useState(0);

  useEffect(() => {
    if (user) {
      const origin = location.state?.from?.pathname || '/leads';
      navigate(origin, { replace: true });
    }
  }, [user, navigate, location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError(null);
    const newErrors = {};
    if (!email) newErrors.email = 'Email address is required';
    else if (!/\S+@\S+\.\S+/.test(email)) newErrors.email = 'Please enter a valid email';
    if (!passcode) newErrors.passcode = 'Passcode is required';
    else if (passcode.length !== 6) newErrors.passcode = 'Passcode must be 6 digits';
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setIsSubmitting(true);
    setStage(1);
    
    try {
      const response = await authApi.verifyLogin(email, passcode);
      setStage(2);
      if (response.success && response.client) {
        setStage(3);
        setTimeout(() => setStage(4), 500);
        login(response.client, rememberMe);
      } else {
        setStage(0);
        setApiError(response.message || 'Invalid credentials');
      }
    } catch (err) {
      setStage(0);
      setApiError('Unable to connect to the portal');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-surface rounded-xl shadow-2xl p-8 relative overflow-hidden">
        <div className="absolute top-0 right-0 -mt-8 -mr-8 w-32 h-32 bg-primary/5 rounded-full"></div>
        
        <div className="relative z-10 text-center mb-8">
          <div className="w-16 h-16 bg-slate-900 rounded-xl flex items-center justify-center shadow-xl mx-auto mb-4">
            <SafeIcon icon={FiShield} className="text-accent text-2xl" />
          </div>
          <h1 className="text-xl font-bold text-ink mb-2">Client Portal</h1>
          <p className="text-sm text-muted">Secure lead management access</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <AnimatePresence mode="wait">
            {apiError && (
              <motion.div 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="p-4 bg-error/5 border border-error/20 rounded-lg flex gap-3 text-error text-sm font-medium"
              >
                <SafeIcon icon={FiAlertCircle} />
                <p>{apiError}</p>
              </motion.div>
            )}
          </AnimatePresence>

          {stage > 0 && (
            <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg flex items-center gap-3">
              <SafeIcon icon={FiLoader} className="text-primary animate-spin" />
              <p className="text-sm font-medium text-primary">
                {stage === 1 && 'Checking credentials...'}
                {stage === 2 && 'Verifying portal access...'}
                {stage === 3 && 'Loading leads...'}
                {stage === 4 && 'Redirecting...'}
              </p>
            </div>
          )}

          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">Business Email</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                <SafeIcon icon={FiMail} />
              </div>
              <input 
                type="email" 
                value={email} 
                onChange={(e) => { setEmail(e.target.value); if (errors.email) setErrors({...errors, email: null}); }}
                placeholder="john@business.com"
                className={`w-full pl-10 pr-4 py-3 bg-slate-50 border rounded-lg text-sm font-medium outline-none focus:ring-2 focus:ring-primary/10 focus:border-primary/30 transition-all ${
                  errors.email ? 'border-error/50 ring-error/10' : 'border-border'
                }`} 
              />
            </div>
            {errors.email && <p className="text-xs font-medium text-error">{errors.email}</p>}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">6-Digit Passcode</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                <SafeIcon icon={FiLock} />
              </div>
              <input 
                type={showPasscode ? "text" : "password"}
                value={passcode} 
                onChange={(e) => { 
                  const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setPasscode(val);
                  if (errors.passcode) setErrors({...errors, passcode: null});
                }}
                placeholder="••••••"
                className={`w-full pl-10 pr-10 py-3 bg-slate-50 border rounded-lg text-sm font-medium outline-none focus:ring-2 focus:ring-primary/10 focus:border-primary/30 transition-all tracking-widest font-mono ${
                  errors.passcode ? 'border-error/50 ring-error/10' : 'border-border'
                }`} 
              />
              <button 
                type="button" 
                onClick={() => setShowPasscode(!showPasscode)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-primary transition-colors"
              >
                <SafeIcon icon={showPasscode ? FiEyeOff : FiEye} />
              </button>
            </div>
            {errors.passcode && <p className="text-xs font-medium text-error">{errors.passcode}</p>}
          </div>

          <div className="flex items-center gap-3">
            <input 
              type="checkbox" 
              id="remember" 
              checked={rememberMe} 
              onChange={() => setRememberMe(!rememberMe)} 
              className="w-4 h-4 rounded border-slate-300 text-primary focus:ring-primary/20" 
            />
            <label htmlFor="remember" className="text-sm font-medium text-slate-700 cursor-pointer">Remember this device</label>
          </div>

          <button 
            type="submit" 
            disabled={isSubmitting}
            className="w-full py-3 bg-gradient-to-r from-primary to-accent text-white rounded-lg font-medium flex items-center justify-center gap-2 shadow-card hover:-translate-y-0.5 transition-premium active:scale-95 disabled:opacity-70"
          >
            {isSubmitting ? (
              <><SafeIcon icon={FiLoader} className="animate-spin" /> Authorizing...</>
            ) : (
              <>Verify Access <SafeIcon icon={FiArrowRight} /></>
            )}
          </button>
        </form>

        <div className="mt-8 pt-6 border-t border-border flex justify-center">
          <Link to="/admin/login" className="flex items-center gap-2 text-sm font-medium text-muted hover:text-primary transition-colors">
            <SafeIcon icon={FiShield} />
            <span>Admin? <span className="underline">Admin Console</span></span>
          </Link>
        </div>
      </div>
    </div>
  );
}